'use client';

import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

import {
  getDataGridMinWidth,
  getVisibleDataColumns,
} from '../bulk-edit-columns';
import type { BulkEditColumnId } from '../bulk-edit.types';
import { useBulkEditLayoutMetrics } from '../bulk-edit-layout-context';
import { bulkEditTableScroll } from '../bulk-edit-styles';

interface BulkEditTableSkeletonProps {
  visibleColumns: Set<BulkEditColumnId>;
  rows?: number;
}

function BulkEditSkeletonCell({ width, className }: { width: number; className?: string }) {
  const { rowPx } = useBulkEditLayoutMetrics();
  return (
    <div
      className={cn('crm-bulk-edit-cell flex shrink-0 items-center px-3', className)}
      style={{ width, height: rowPx }}
    >
      <Skeleton className="h-4 w-[70%] max-w-full rounded-md" />
    </div>
  );
}

export function BulkEditTableSkeleton({ visibleColumns, rows = 12 }: BulkEditTableSkeletonProps) {
  const dataColumns = getVisibleDataColumns(visibleColumns);
  const { headerPx, rowPx, checkboxPx, leftPx } = useBulkEditLayoutMetrics();
  const gridMinWidth = leftPx + getDataGridMinWidth(visibleColumns);

  return (
    <div
      className={cn(bulkEditTableScroll, 'crm-bulk-edit-skeleton')}
      aria-busy="true"
      aria-label="Loading products"
    >
      <div style={{ minWidth: gridMinWidth }}>
        <div
          className="crm-bulk-edit-row crm-bulk-edit-row--head flex w-max min-w-full"
          style={{ height: headerPx }}
        >
          <div className="crm-bulk-edit-frozen flex shrink-0" style={{ width: leftPx }}>
            <div
              className="flex shrink-0 items-center justify-center"
              style={{ width: checkboxPx, height: headerPx }}
            >
              <Skeleton className="size-4 rounded-[4px]" />
            </div>
            <div className="flex min-w-0 flex-1 items-center px-3">
              <Skeleton className="h-3.5 w-24 rounded-md" />
            </div>
          </div>
          <div className="flex shrink-0">
            {dataColumns.map((col) => (
              <div
                key={col.id}
                className="crm-bulk-edit-th flex shrink-0 items-center px-3"
                style={{ width: col.width, height: headerPx }}
              >
                <Skeleton className="h-3 w-[60%] rounded-md" />
              </div>
            ))}
          </div>
        </div>

        {Array.from({ length: rows }).map((_, index) => (
          <div
            key={index}
            className="crm-bulk-edit-row flex w-max min-w-full border-b border-border/55"
            style={{ height: rowPx }}
          >
            <div className="crm-bulk-edit-frozen flex shrink-0 bg-card" style={{ width: leftPx }}>
              <div
                className="flex shrink-0 items-center justify-center"
                style={{ width: checkboxPx, height: rowPx }}
              >
                <Skeleton className="size-4 rounded-[4px]" />
              </div>
              <div className="flex min-w-0 flex-1 items-center gap-2 px-2">
                <Skeleton className="size-8 shrink-0 rounded-md" />
                <Skeleton className="h-4 w-[75%] max-w-[14rem] rounded-md" />
              </div>
            </div>
            <div className="flex shrink-0">
              {dataColumns.map((col) => (
                <BulkEditSkeletonCell key={col.id} width={col.width} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function BulkEditToolbarSkeleton() {
  return (
    <div className="crm-bulk-edit-card__toolbar" aria-hidden>
      <Skeleton className="h-9 w-full max-w-[22rem] rounded-md" />
      <Skeleton className="h-9 w-[9.5rem] rounded-md" />
    </div>
  );
}

export function BulkEditFooterSkeleton() {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 px-1 py-0.5" aria-hidden>
      <Skeleton className="h-4 w-28 rounded-md" />
      <div className="flex items-center gap-2">
        <Skeleton className="h-8 w-8 rounded-md" />
        <Skeleton className="h-8 w-8 rounded-md" />
        <Skeleton className="h-4 w-16 rounded-md" />
        <Skeleton className="h-8 w-8 rounded-md" />
        <Skeleton className="h-8 w-8 rounded-md" />
      </div>
    </div>
  );
}
